
import React from 'react';
import { 
  Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Box, Button, TextField, InputAdornment, Chip, Avatar, IconButton, Menu, MenuItem,
  Tabs, Tab, Snackbar, Alert
} from '@mui/material';
import { Search, Add, MoreVert, CheckCircle, AccessTime, Cancel, Download } from '@mui/icons-material';
import NewBookingDialog from '../components/NewBookingDialog';
import ViewBookingDialog from '../components/ViewBookingDialog';
import EditBookingDialog from '../components/EditBookingDialog';
import CheckInDialog from '../components/CheckInDialog';
import CheckOutDialog from '../components/CheckOutDialog';

const mockBookings = [
  { id: 'BK001', guest: 'John Doe', email: 'john@email.com', room: 'Deluxe 101', checkIn: '2025-11-15', checkOut: '2025-11-18', status: 'Checked In', amount: '$450', nights: 3 },
  { id: 'BK002', guest: 'Jane Smith', email: 'jane@email.com', room: 'Suite 202', checkIn: '2025-11-16', checkOut: '2025-11-20', status: 'Confirmed', amount: '$680', nights: 4 },
  { id: 'BK003', guest: 'Alice Brown', email: 'alice@email.com', room: 'Standard 303', checkIn: '2025-11-14', checkOut: '2025-11-17', status: 'Checked Out', amount: '$320', nights: 3 },
  { id: 'BK004', guest: 'Bob Wilson', email: 'bob@email.com', room: 'Deluxe 105', checkIn: '2025-11-18', checkOut: '2025-11-22', status: 'Confirmed', amount: '$600', nights: 4 },
  { id: 'BK005', guest: 'Carol Davis', email: 'carol@email.com', room: 'Suite 301', checkIn: '2025-11-16', checkOut: '2025-11-19', status: 'Pending', amount: '$750', nights: 3 },
  { id: 'BK006', guest: 'David Miller', email: 'david@email.com', room: 'Standard 102', checkIn: '2025-11-19', checkOut: '2025-11-21', status: 'Confirmed', amount: '$280', nights: 2 },
];

export default function Bookings() {
  const [tabValue, setTabValue] = React.useState(0);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedBooking, setSelectedBooking] = React.useState(null);
  const [bookings, setBookings] = React.useState(mockBookings);
  const [newBookingOpen, setNewBookingOpen] = React.useState(false);
  const [viewBookingOpen, setViewBookingOpen] = React.useState(false);
  const [editBookingOpen, setEditBookingOpen] = React.useState(false);
  const [checkInOpen, setCheckInOpen] = React.useState(false);
  const [checkOutOpen, setCheckOutOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = React.useState('');

  const handleBookingCreated = (newBooking) => {
    setBookings([newBooking, ...bookings]);
    setSnackbar({
      open: true,
      message: `Booking ${newBooking.id} created successfully!`,
      severity: 'success',
    });
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setViewBookingOpen(true);
    setAnchorEl(null);
  };

  const handleEditBooking = (booking) => {
    setSelectedBooking(booking);
    setEditBookingOpen(true);
    setAnchorEl(null);
  };

  const handleSaveBooking = (updatedBooking) => {
    setBookings(bookings.map(b => b.id === updatedBooking.id ? updatedBooking : b));
    setSnackbar({
      open: true,
      message: `Booking ${updatedBooking.id} updated successfully!`,
      severity: 'success',
    });
  };

  const handleMenuClick = (event, booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleCheckIn = (booking) => {
    setSelectedBooking(booking);
    setCheckInOpen(true);
    setAnchorEl(null);
  };

  const handleCheckOut = (booking) => {
    setSelectedBooking(booking);
    setCheckOutOpen(true);
    setAnchorEl(null);
  };

  const handleCheckInComplete = (updatedBooking) => {
    setBookings(bookings.map(b => b.id === updatedBooking.id ? updatedBooking : b));
    setSnackbar({
      open: true,
      message: `Guest ${updatedBooking.guest} checked in successfully to ${updatedBooking.room}!`,
      severity: 'success',
    });
  };

  const handleCheckOutComplete = (updatedBooking) => {
    setBookings(bookings.map(b => b.id === updatedBooking.id ? updatedBooking : b));
    setSnackbar({
      open: true,
      message: `Guest ${updatedBooking.guest} checked out successfully!`,
      severity: 'success',
    });
  };

  const filteredBookings = bookings.filter((booking) => {
    const query = searchQuery.toLowerCase();
    return (
      booking.id.toLowerCase().includes(query) ||
      booking.guest.toLowerCase().includes(query) ||
      booking.room.toLowerCase().includes(query) ||
      booking.email.toLowerCase().includes(query)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Checked In': return 'primary';
      case 'Confirmed': return 'success';
      case 'Checked Out': return 'default';
      case 'Pending': return 'warning';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Checked In': return <CheckCircle />;
      case 'Confirmed': return <CheckCircle />;
      case 'Pending': return <AccessTime />;
      case 'Cancelled': return <Cancel />;
      default: return null;
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Bookings Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all hotel bookings and reservations
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<Download />}>Export</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setNewBookingOpen(true)}>
            New Booking
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="All Bookings" />
            <Tab label="Check-ins Today" />
            <Tab label="Check-outs Today" />
          </Tabs>
        </Box>
        <Box p={2}>
          <TextField
            fullWidth
            placeholder="Search bookings by guest name, room, or booking ID..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Booking ID</strong></TableCell>
              <TableCell><strong>Guest Details</strong></TableCell>
              <TableCell><strong>Room</strong></TableCell>
              <TableCell><strong>Check-in</strong></TableCell>
              <TableCell><strong>Check-out</strong></TableCell>
              <TableCell><strong>Nights</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBookings.map((booking) => (
              <TableRow key={booking.id} hover>
                <TableCell><strong>{booking.id}</strong></TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: '#1976d2' }}>
                      {booking.guest[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{booking.guest}</Typography>
                      <Typography variant="caption" color="text.secondary">{booking.email}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{booking.room}</TableCell>
                <TableCell>{booking.checkIn}</TableCell>
                <TableCell>{booking.checkOut}</TableCell>
                <TableCell>{booking.nights}</TableCell>
                <TableCell><strong>{booking.amount}</strong></TableCell>
                <TableCell>
                  <Chip 
                    label={booking.status} 
                    size="small"
                    color={getStatusColor(booking.status)}
                    icon={getStatusIcon(booking.status)}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Button size="small" variant="outlined" onClick={() => handleViewBooking(booking)}>
                      View
                    </Button>
                    <IconButton size="small" onClick={(e) => handleMenuClick(e, booking)}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => handleEditBooking(selectedBooking)}>Edit Booking</MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); }}>Send Confirmation</MenuItem>
        <MenuItem 
          onClick={() => handleCheckIn(selectedBooking)}
          disabled={selectedBooking?.status === 'Checked In' || selectedBooking?.status === 'Checked Out'}
        >
          Check In
        </MenuItem>
        <MenuItem 
          onClick={() => handleCheckOut(selectedBooking)}
          disabled={selectedBooking?.status !== 'Checked In'}
        >
          Check Out
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); }}>Cancel Booking</MenuItem>
      </Menu>

      <NewBookingDialog
        open={newBookingOpen}
        onClose={() => setNewBookingOpen(false)}
        onBookingCreated={handleBookingCreated}
      />

      <ViewBookingDialog
        open={viewBookingOpen}
        onClose={() => setViewBookingOpen(false)}
        booking={selectedBooking}
        onEdit={handleEditBooking}
      />

      <EditBookingDialog
        open={editBookingOpen}
        onClose={() => setEditBookingOpen(false)}
        booking={selectedBooking}
        onSave={handleSaveBooking}
      />

      <CheckInDialog
        open={checkInOpen}
        onClose={() => setCheckInOpen(false)}
        booking={selectedBooking}
        onCheckIn={handleCheckInComplete}
      />

      <CheckOutDialog
        open={checkOutOpen}
        onClose={() => setCheckOutOpen(false)}
        booking={selectedBooking}
        onCheckOut={handleCheckOutComplete}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
